// The demo backend. `demoRequest` stands in for the network `request()` in
// client.ts: it parses the method+path the client would have sent, dispatches to
// an in-browser handler that mutates the demo store, and returns the same shapes
// the real API returns. All the hard rules (pricing, membership state, schedule
// generation, check-in windows, trail-number prediction) are reused verbatim
// from @hash/shared, so the demo behaves like production without a server.

import {
  doorAmountOwed,
  isValidEmail,
  kennelDisplayName,
  membershipMissingFields,
  membershipState,
  purchasablePeriods,
  scheduleOccurrences,
  trailStartAt,
  type AttendanceReport,
  type Attendance,
  type ReportTrailRow,
  type Hasher,
  type Kennel,
  type Membership,
  type MembershipPeriod,
  type MembershipScheme,
  type Payment,
  type Role,
  type RoleAssignment,
  type RoleAuditRecord,
  type Schedule,
  type Trail,
  type TrailHistoryEntry,
} from "@hash/shared";
import { getClaims, type DemoClaims } from "../../auth/demo-auth";
import { genId } from "./ids";
import { getDb, persist } from "./store";

// --- error + dispatch plumbing ----------------------------------------------

// Matches the shape the real client throws — `Error("<status>: <json body>")` —
// so web/src/api/errors.ts parses demo errors exactly like network ones.
class DemoHttpError extends Error {
  constructor(status: number, payload: Record<string, unknown>) {
    super(`${status}: ${JSON.stringify(payload)}`);
  }
}
function fail(
  status: number,
  error: string,
  code?: string,
  extra?: Record<string, unknown>
): never {
  throw new DemoHttpError(status, { error, ...(code ? { code } : {}), ...extra });
}

interface Ctx {
  params: Record<string, string>;
  query: URLSearchParams;
  body: any;
  method: string;
}
type Handler = (ctx: Ctx) => unknown;

interface Route {
  method: string;
  segments: string[];
  handler: Handler;
}
const routes: Route[] = [];
function route(method: string, pattern: string, handler: Handler): void {
  routes.push({ method, segments: pattern.split("/").filter(Boolean), handler });
}

export async function demoRequest<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const [rawPath, search = ""] = path.split("?");
  const query = new URLSearchParams(search);
  const segments = rawPath.split("/").filter(Boolean).map(decodeURIComponent);
  const body =
    typeof init.body === "string" && init.body ? JSON.parse(init.body) : undefined;

  for (const r of routes) {
    if (r.method !== method || r.segments.length !== segments.length) continue;
    const params: Record<string, string> = {};
    let matched = true;
    for (let i = 0; i < r.segments.length; i++) {
      const seg = r.segments[i];
      if (seg.startsWith(":")) params[seg.slice(1)] = segments[i];
      else if (seg !== segments[i]) {
        matched = false;
        break;
      }
    }
    if (!matched) continue;
    // Simulate a little latency-free async boundary so callers awaiting work.
    const result = r.handler({ params, query, body, method });
    return result as T;
  }
  return fail(404, `No demo route for ${method} ${rawPath}`);
}

// --- shared helpers ----------------------------------------------------------

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function nowIso(): string {
  return new Date().toISOString();
}
function auth(): DemoClaims {
  const c = getClaims();
  if (!c) return fail(401, "Not signed in");
  return c;
}
function isMismanOf(c: DemoClaims, kennelId: string): boolean {
  return c.role === "admin" || c.mismanKennels.includes(kennelId);
}
function requireAdmin(c: DemoClaims): void {
  if (c.role !== "admin") fail(403, "Admin only");
}
function requireMismanOf(c: DemoClaims, kennelId: string): void {
  if (!isMismanOf(c, kennelId)) fail(403, "Not a misman of this kennel");
}
function requireAnyMisman(c: DemoClaims): void {
  if (c.role !== "admin" && c.mismanKennels.length === 0) fail(403, "Misman only");
}

function kennelIdOf(trailId: string): string {
  return trailId.slice(0, trailId.lastIndexOf("#"));
}
function findKennel(id: string): Kennel {
  const k = getDb().kennels.find((x) => x.id === id);
  if (!k) fail(404, "Kennel not found");
  return k;
}
function findHasher(id: string): Hasher {
  const h = getDb().hashers.find((x) => x.id === id);
  if (!h) fail(404, "Hasher not found");
  return h;
}
function findTrail(id: string): Trail {
  const t = getDb().trails.find((x) => x.id === id);
  if (!t) fail(404, "Event not found");
  return t;
}
function membershipOf(hasherId: string, kennelId: string): Membership | undefined {
  return getDb().memberships.find(
    (m) => m.hasherId === hasherId && m.kennelId === kennelId
  );
}
function hareNamesOf(t: Trail): string[] {
  const db = getDb();
  return t.hareHasherIds
    .map((id) => db.hashers.find((h) => h.id === id && !h.deletedAt)?.name)
    .filter((n): n is string => Boolean(n));
}
function listShape(t: Trail): Trail {
  return { ...t, hareNames: hareNamesOf(t) };
}
function nameTakenInKennel(
  name: string,
  kennelId: string,
  exceptId?: string
): boolean {
  const lc = name.trim().toLowerCase();
  return getDb().hashers.some(
    (h) =>
      h.id !== exceptId &&
      !h.deletedAt &&
      h.homeKennelId === kennelId &&
      h.name.toLowerCase() === lc
  );
}
function phoneConflict(phone: string, exceptId?: string): Hasher | undefined {
  return getDb().hashers.find(
    (h) => h.id !== exceptId && !h.deletedAt && h.phone === phone
  );
}
function emailConflict(email: string, exceptId?: string): Hasher | undefined {
  const lc = email.trim().toLowerCase();
  return getDb().hashers.find(
    (h) => h.id !== exceptId && !h.deletedAt && h.email?.toLowerCase() === lc
  );
}

// Recompute a hasher's denormalized lastAttendedAt from remaining attendance.
function recomputeLastAttended(hasherId: string): void {
  const db = getDb();
  const dates = db.attendance
    .filter((a) => a.hasherId === hasherId)
    .map((a) => db.trails.find((t) => t.id === a.trailId)?.date)
    .filter((d): d is string => Boolean(d))
    .sort();
  const h = db.hashers.find((x) => x.id === hasherId);
  if (h) {
    if (dates.length) h.lastAttendedAt = dates[dates.length - 1];
    else delete h.lastAttendedAt;
  }
}

// Door/special payment for one (event, hasher), reused by check-in + setPaid.
function recordEventPayment(trail: Trail, hasher: Hasher, recordedBy: string): void {
  const db = getDb();
  const kennel = db.kennels.find((k) => k.id === trail.kennelId);
  const state = membershipState(membershipOf(hasher.id, trail.kennelId), trail.date);
  const att = db.attendance.find(
    (a) => a.trailId === trail.id && a.hasherId === hasher.id
  );
  const amount = doorAmountOwed(state, kennel?.doorFee, trail, {
    virgin: att?.virgin,
    virginsFreeDoor: kennel?.virginsFreeDoor,
  });
  const existing = db.payments.find(
    (p) => p.trailId === trail.id && p.hasherId === hasher.id && p.state === "paid"
  );
  if (existing) return;
  db.payments.push({
    id: genId("pay"),
    hasherId: hasher.id,
    kennelId: trail.kennelId,
    trailId: trail.id,
    kind: trail.specialFee ? "special" : "door",
    amount,
    method: "cash",
    state: "paid",
    recordedByHasherId: recordedBy,
    createdAt: nowIso(),
  });
}
function reverseEventPayment(trailId: string, hasherId: string, by: string): void {
  for (const p of getDb().payments) {
    if (p.trailId === trailId && p.hasherId === hasherId && p.state === "paid") {
      p.state = "reversed";
      p.reversedAt = nowIso();
      p.reversedByHasherId = by;
    }
  }
}

function auditRole(rec: Omit<RoleAuditRecord, "id" | "at">): void {
  getDb().roleAudit.push({ ...rec, id: genId("ra"), at: nowIso() });
}

// =============================================================================
// Hashers
// =============================================================================

route("GET", "/hashers", ({ query }) => {
  auth();
  const kennelId = query.get("kennelId") ?? undefined;
  let hashers = getDb().hashers.filter((h) => !h.deletedAt);
  if (kennelId) hashers = hashers.filter((h) => h.homeKennelId === kennelId);
  hashers = [...hashers].sort((a, b) => a.name.localeCompare(b.name));
  return { hashers }; // roster is small — one unpaginated page
});

route("GET", "/hashers/recent", ({ query }) => {
  auth();
  const weeks = Number(query.get("weeks") ?? 4);
  const cutoff = new Date(Date.now() - weeks * 7 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  const hashers = getDb()
    .hashers.filter((h) => !h.deletedAt && (h.lastAttendedAt ?? "") >= cutoff)
    .sort((a, b) => (b.lastAttendedAt ?? "").localeCompare(a.lastAttendedAt ?? ""));
  return { hashers };
});

// A hasher's record: the hasher plus their membership rows across kennels
// (Story 10.1). Backs the Manage screen's memberships section.
route("GET", "/hashers/:id/memberships", ({ params }) => {
  requireAnyMisman(auth());
  const hasher = findHasher(params.id);
  const memberships = getDb().memberships.filter(
    (m) => m.hasherId === hasher.id
  );
  return { hasher, memberships };
});

route("POST", "/hashers", ({ body }) => {
  auth();
  const kennel = findKennel(body.homeKennelId);
  if (!body.name?.trim()) fail(400, "Name is required");
  if (nameTakenInKennel(body.name, kennel.id)) {
    fail(409, `${body.name} already exists in ${kennel.name}`, "DUPLICATE_NAME");
  }
  if (body.phone) {
    const conflict = phoneConflict(body.phone);
    if (conflict) {
      fail(409, `That number already belongs to ${conflict.name}`, "PHONE_CONFLICT");
    }
  }
  // nerdName + email are optional at creation (required only before a
  // membership is recorded). Blank stays absent; email is shape- and
  // uniqueness-checked when present.
  const nerdName = body.nerdName?.trim() || undefined;
  const email = body.email?.trim() || undefined;
  if (email) {
    if (!isValidEmail(email)) fail(400, "That email doesn't look right");
    const clash = emailConflict(email);
    if (clash) {
      fail(409, `That email already belongs to ${clash.name}`, "EMAIL_CONFLICT");
    }
  }
  const hasher: Hasher = {
    id: genId("h"),
    name: body.name.trim(),
    displayName: body.displayName?.trim() || undefined,
    nerdName,
    email,
    homeKennelId: kennel.id,
    homeKennel: kennel.name,
    phone: body.phone,
    role: "hare",
    visitor: body.visitor ?? !kennel.managed,
    accountState: body.phone ? "unclaimed" : "lite",
    createdAt: nowIso(),
  };
  getDb().hashers.push(hasher);
  persist();
  return hasher;
});

route("PATCH", "/hashers/:id", ({ params, body }) => {
  requireAnyMisman(auth());
  const h = findHasher(params.id);
  if (body.name !== undefined) {
    if (!body.name.trim()) fail(400, "Name is required");
    const kennelId = body.homeKennelId ?? h.homeKennelId;
    if (nameTakenInKennel(body.name, kennelId, h.id)) {
      fail(409, `${body.name} already exists in that kennel`, "DUPLICATE_NAME");
    }
    h.name = body.name.trim();
  }
  if (body.homeKennelId !== undefined && body.homeKennelId !== h.homeKennelId) {
    const k = findKennel(body.homeKennelId);
    h.homeKennelId = k.id;
    h.homeKennel = k.name;
  }
  if (body.visitor !== undefined) h.visitor = body.visitor;
  // Blank clears the field; a supplied email is shape- and uniqueness-checked.
  if (body.displayName !== undefined) {
    h.displayName = body.displayName.trim() || undefined;
  }
  if (body.nerdName !== undefined) {
    h.nerdName = body.nerdName.trim() || undefined;
  }
  if (body.email !== undefined) {
    const email = body.email.trim() || undefined;
    if (email) {
      if (!isValidEmail(email)) fail(400, "That email doesn't look right");
      const clash = emailConflict(email, h.id);
      if (clash) {
        fail(409, `That email already belongs to ${clash.name}`, "EMAIL_CONFLICT");
      }
    }
    h.email = email;
  }
  persist();
  return h;
});

route("DELETE", "/hashers/:id", ({ params }) => {
  requireAdmin(auth());
  const db = getDb();
  const h = findHasher(params.id);
  const hasHistory =
    db.attendance.some((a) => a.hasherId === h.id) ||
    db.payments.some((p) => p.hasherId === h.id);
  if (hasHistory) h.deletedAt = nowIso();
  else db.hashers = db.hashers.filter((x) => x.id !== h.id);
  persist();
  return { deleted: true };
});

route("POST", "/hashers/:id/phone", ({ params, body }) => {
  auth();
  const h = findHasher(params.id);
  if (h.accountState === "claimed") {
    fail(409, "This hasher has claimed their account", "ALREADY_CLAIMED");
  }
  const conflict = phoneConflict(body.phone, h.id);
  if (conflict) {
    fail(409, `That number already belongs to ${conflict.name}`, "PHONE_CONFLICT");
  }
  h.phone = body.phone;
  h.accountState = "unclaimed";
  persist();
  return { hasherId: h.id, phone: h.phone, accountState: "unclaimed" as const };
});

// =============================================================================
// Me (self-service)
// =============================================================================

route("GET", "/me", () => findHasher(auth().hasherId));

route("POST", "/me/phone", ({ body }) => {
  const c = auth();
  const conflict = phoneConflict(body.phone, c.hasherId);
  if (conflict) {
    fail(409, `That number already belongs to ${conflict.name}`, "PHONE_CONFLICT");
  }
  const h = findHasher(c.hasherId);
  h.phone = body.phone;
  persist();
  return { hasherId: h.id, phone: h.phone };
});

route("POST", "/me/attendance", ({ body }) => {
  const c = auth();
  const trail = findTrail(body.trailId);
  const h = findHasher(c.hasherId);
  const db = getDb();
  let att = db.attendance.find(
    (a) => a.trailId === trail.id && a.hasherId === h.id
  );
  if (!att) {
    att = {
      trailId: trail.id,
      hasherId: h.id,
      nameSnapshot: h.name,
      paid: false,
      virgin: !db.attendance.some((a) => a.hasherId === h.id),
      visitor: h.visitor || h.homeKennelId !== trail.kennelId,
      checkedInAt: nowIso(),
      checkedInByHasherId: h.id,
    };
    db.attendance.push(att);
    recomputeLastAttended(h.id);
  }
  const kennel = db.kennels.find((k) => k.id === trail.kennelId);
  const state = membershipState(membershipOf(h.id, trail.kennelId), trail.date);
  const owed = att.paid
    ? 0
    : doorAmountOwed(state, kennel?.doorFee, trail, {
        virgin: att.virgin,
        virginsFreeDoor: kennel?.virginsFreeDoor,
      });
  persist();
  return { attendance: att, owed };
});

route("GET", "/me/history", () => {
  const c = auth();
  const db = getDb();
  const today = new Date().toISOString().slice(0, 10);
  const mine = db.attendance.filter((a) => a.hasherId === c.hasherId);
  const events = mine
    .map((a) => {
      const t = db.trails.find((x) => x.id === a.trailId);
      return {
        trailId: a.trailId,
        eventType: t?.eventType,
        number: t?.number,
        name: t?.name,
        date: t?.date,
        kennel: t?.kennel,
        title: t?.title,
        nameSnapshot: a.nameSnapshot,
        virgin: a.virgin,
        visitor: a.visitor,
        // Gated to date <= today, same as the "hared" stat below — a hare on
        // a not-yet-run trail is scheduled, not done.
        hare: (t?.hareHasherIds.includes(c.hasherId) ?? false) && (t?.date ?? "") <= today,
        checkedInAt: a.checkedInAt,
      };
    })
    .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  // Count stats reflect only trails already run (date <= today) — a future
  // trail a hasher is auto-checked-in on as a hare stays in the list but out
  // of the counts, so total/perKennel/12-month all agree. Mirrors me.ts.
  const completed = events.filter((e) => (e.date ?? "") <= today);
  const perKennel: Record<string, number> = {};
  for (const e of completed) if (e.kennel) perKennel[e.kennel] = (perKennel[e.kennel] ?? 0) + 1;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const hared = db.trails.filter(
    (t) => t.hareHasherIds.includes(c.hasherId) && t.date <= today
  );
  const lastHaredDate = hared
    .map((t) => t.date)
    .sort()
    .at(-1);
  return {
    events,
    memberships: db.memberships.filter((m) => m.hasherId === c.hasherId),
    payments: db.payments.filter((p) => p.hasherId === c.hasherId),
    stats: {
      total: completed.length,
      last12Months: completed.filter((e) => (e.date ?? "") >= cutoffStr).length,
      virgins: completed.filter((e) => e.virgin).length,
      hared: hared.length,
      lastHaredDate,
      perKennel,
    },
  };
});

// =============================================================================
// Kennels
// =============================================================================

route("GET", "/kennels", ({ query }) => {
  auth();
  const q = (query.get("q") ?? "").trim().toLowerCase();
  let kennels = [...getDb().kennels];
  if (q) {
    kennels = kennels.filter(
      (k) =>
        k.name.toLowerCase().includes(q) ||
        (k.shortName?.toLowerCase().includes(q) ?? false)
    );
  }
  kennels.sort((a, b) => a.name.localeCompare(b.name));
  return { kennels };
});

route("POST", "/kennels", ({ body }) => {
  requireAnyMisman(auth());
  if (!body.name?.trim()) fail(400, "Name is required");
  const lc = body.name.trim().toLowerCase();
  if (getDb().kennels.some((k) => k.name.toLowerCase() === lc)) {
    fail(409, `A kennel named ${body.name} already exists`, "DUPLICATE_NAME");
  }
  const kennel: Kennel = {
    id: genId("k"),
    name: body.name.trim(),
    shortName: body.shortName?.trim() || undefined,
    managed: body.managed ?? false,
    city: body.city,
    region: body.region,
    founded: body.founded,
    doorFee: body.doorFee,
    createdAt: nowIso(),
  };
  getDb().kennels.push(kennel);
  persist();
  return kennel;
});

route("PATCH", "/kennels/:id", ({ params, body }) => {
  requireMismanOf(auth(), params.id);
  const db = getDb();
  const k = findKennel(params.id);
  const beforeDisplay = kennelDisplayName(k);
  if (body.name !== undefined && body.name.trim() !== k.name) {
    const lc = body.name.trim().toLowerCase();
    if (db.kennels.some((x) => x.id !== k.id && x.name.toLowerCase() === lc)) {
      fail(409, `A kennel named ${body.name} already exists`, "DUPLICATE_NAME");
    }
    k.name = body.name.trim();
  }
  if (body.shortName !== undefined) k.shortName = body.shortName.trim() || undefined;
  for (const f of ["city", "region", "founded", "doorFee", "virginsFreeDoor"] as const) {
    if (body[f] !== undefined) (k as any)[f] = body[f];
  }
  // Fan out the cached names only when the display name actually changed
  // (hashers cache the full name, trails the display name) — same rule as prod.
  const afterDisplay = kennelDisplayName(k);
  if (afterDisplay !== beforeDisplay || body.name !== undefined) {
    for (const h of db.hashers) if (h.homeKennelId === k.id) h.homeKennel = k.name;
    for (const t of db.trails) if (t.kennelId === k.id) t.kennel = afterDisplay;
  }
  persist();
  return k;
});

route("POST", "/kennels/:id/promote", ({ params }) => {
  requireAdmin(auth());
  const k = findKennel(params.id);
  k.managed = true;
  persist();
  return k;
});

route("DELETE", "/kennels/:id", ({ params }) => {
  requireAdmin(auth());
  const db = getDb();
  findKennel(params.id);
  db.kennels = db.kennels.filter((k) => k.id !== params.id);
  persist();
  return { deleted: true };
});

route("PUT", "/kennels/:id/schedule", ({ params, body }) => {
  requireMismanOf(auth(), params.id);
  const k = findKennel(params.id);
  k.schedule = body as Schedule;
  const generated = generateTrails(k);
  persist();
  return { schedule: k.schedule, generated };
});

route("POST", "/kennels/:id/schedule/extend", ({ params, body }) => {
  requireMismanOf(auth(), params.id);
  const k = findKennel(params.id);
  if (!k.schedule) fail(400, "No schedule to extend");
  k.schedule.horizonMonths += Number(body.months ?? 0);
  const generated = generateTrails(k);
  persist();
  return { generated };
});

// Generate placeholder trails out to the schedule horizon, skipping dates that
// already have an event and never touching a trail that's had a hare. Mirrors
// the server's generation rules (Stories 3.4/3.5).
function generateTrails(kennel: Kennel): number {
  const sched = kennel.schedule;
  if (!sched) return 0;
  const db = getDb();
  const from = today();
  const to = new Date();
  to.setUTCMonth(to.getUTCMonth() + sched.horizonMonths);
  const toStr = to.toISOString().slice(0, 10);
  const dates = scheduleOccurrences(sched.recurrence, from, toStr);
  const existing = new Set(
    db.trails.filter((t) => t.kennelId === kennel.id).map((t) => t.date)
  );
  let maxNumber = Math.max(
    0,
    ...db.trails
      .filter((t) => t.kennelId === kennel.id && typeof t.number === "number")
      .map((t) => t.number as number)
  );
  let generated = 0;
  for (const date of dates) {
    if (existing.has(date)) continue;
    maxNumber += 1;
    db.trails.push({
      id: `${kennel.id}#${maxNumber}`,
      eventType: "trail",
      kennelId: kennel.id,
      kennel: kennelDisplayName(kennel),
      number: maxNumber,
      date,
      startAt: trailStartAt(date, sched.time, sched.timeZone) ?? undefined,
      hareHasherIds: [],
      everHadHare: false,
      startLocation: "",
      version: 0,
      createdAt: nowIso(),
      createdByHasherId: getClaims()?.hasherId ?? "h_admin",
    });
    generated += 1;
  }
  return generated;
}

// =============================================================================
// Misman roster (kennel-scoped roles)
// =============================================================================

route("GET", "/kennels/:id/misman", ({ params }) => {
  auth();
  return {
    misman: getDb().roleAssignments.filter((r) => r.kennelId === params.id),
  };
});

route("POST", "/kennels/:id/misman", ({ params, body }) => {
  const c = auth();
  requireMismanOf(c, params.id);
  const kennel = findKennel(params.id);
  const h = findHasher(body.hasherId);
  if (h.accountState !== "claimed") {
    fail(400, "A misman must be a hasher who can log in", "NOT_CLAIMED");
  }
  const db = getDb();
  if (
    db.roleAssignments.some(
      (r) => r.hasherId === h.id && r.kennelId === kennel.id
    )
  ) {
    fail(409, `${h.name} is already a misman of ${kennel.name}`, "ALREADY_MISMAN");
  }
  const assignment: RoleAssignment = {
    hasherId: h.id,
    kennelId: kennel.id,
    hasherName: h.name,
    kennelName: kennel.name,
    grantedByHasherId: c.hasherId,
    grantedAt: nowIso(),
  };
  db.roleAssignments.push(assignment);
  auditRole({
    action: "grant",
    role: "misman",
    targetHasherId: h.id,
    targetName: h.name,
    kennelId: kennel.id,
    kennelName: kennel.name,
    actorHasherId: c.hasherId,
    actorName: findHasher(c.hasherId).name,
  });
  persist();
  return assignment;
});

route("DELETE", "/kennels/:id/misman/:hasherId", ({ params }) => {
  const c = auth();
  requireMismanOf(c, params.id);
  const db = getDb();
  const existing = db.roleAssignments.find(
    (r) => r.kennelId === params.id && r.hasherId === params.hasherId
  );
  db.roleAssignments = db.roleAssignments.filter(
    (r) => !(r.kennelId === params.id && r.hasherId === params.hasherId)
  );
  if (existing) {
    auditRole({
      action: "revoke",
      role: "misman",
      targetHasherId: existing.hasherId,
      targetName: existing.hasherName,
      kennelId: existing.kennelId,
      kennelName: existing.kennelName,
      actorHasherId: c.hasherId,
      actorName: findHasher(c.hasherId).name,
    });
  }
  persist();
  return { revoked: true };
});

// =============================================================================
// Membership schemes + payments
// =============================================================================

function purchasableFor(scheme: MembershipScheme): MembershipPeriod[] {
  return purchasablePeriods(scheme, today()).map((p) => ({
    id: `${scheme.id}#${p.start}`,
    schemeId: scheme.id,
    kennelIds: scheme.kennelIds,
    start: p.start,
    end: p.end,
    price: scheme.price,
    materializedAt: nowIso(),
  }));
}

route("GET", "/schemes", ({ query }) => {
  auth();
  const kennelId = query.get("kennelId") ?? undefined;
  let schemes = getDb().schemes;
  if (kennelId) schemes = schemes.filter((s) => s.kennelIds.includes(kennelId));
  return {
    schemes: schemes.map((s) => ({ ...s, purchasable: purchasableFor(s) })),
  };
});

route("POST", "/schemes", ({ body }) => {
  requireAnyMisman(auth());
  const scheme: MembershipScheme = {
    id: genId("s"),
    name: body.name,
    kennelIds: body.kennelIds ?? [],
    price: body.price,
    recurrence: body.recurrence,
    periods: body.periods,
    windowDays: body.windowDays,
    createdAt: nowIso(),
  };
  getDb().schemes.push(scheme);
  persist();
  return scheme;
});

route("PATCH", "/schemes/:id", ({ params, body }) => {
  requireAnyMisman(auth());
  const s = getDb().schemes.find((x) => x.id === params.id);
  if (!s) fail(404, "Scheme not found");
  for (const f of [
    "name",
    "kennelIds",
    "price",
    "recurrence",
    "periods",
    "windowDays",
    "retired",
  ] as const) {
    if (body[f] !== undefined) (s as any)[f] = body[f];
  }
  s.updatedAt = nowIso();
  persist();
  return s;
});

route("POST", "/kennels/:id/memberships", ({ params, body }) => {
  const c = auth();
  requireMismanOf(c, params.id);
  const kennel = findKennel(params.id);
  const h = findHasher(body.hasherId);
  // A nerd name and email are required before a membership can be recorded.
  const missing = membershipMissingFields(h);
  if (missing.length) {
    fail(400, `${h.name} needs a ${missing.join(" and ")} first`, "MISSING_FIELDS");
  }
  const db = getDb();
  const schemes = db.schemes.filter((s) => s.kennelIds.includes(kennel.id));
  // Resolve the period: the requested one, else the first purchasable period.
  let scheme: MembershipScheme | undefined;
  let period: MembershipPeriod | undefined;
  for (const s of schemes) {
    const ps = purchasableFor(s);
    const found = body.periodId
      ? ps.find((p) => p.id === body.periodId)
      : ps.find((p) => p.start <= today() && today() <= p.end) ?? ps[0];
    if (found) {
      scheme = s;
      period = found;
      break;
    }
  }
  if (!scheme || !period) fail(400, "No purchasable period for this kennel");

  // Append the coverage interval to the hasher's membership at every kennel the
  // scheme covers (a paid period grants member status across all of them).
  for (const kid of scheme!.kennelIds) {
    let m = db.memberships.find((x) => x.hasherId === h.id && x.kennelId === kid);
    if (!m) {
      m = {
        id: `${h.id}#${kid}`,
        hasherId: h.id,
        kennelId: kid,
        periods: [],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      };
      db.memberships.push(m);
    }
    if (!m.periods.some((p) => p.periodId === period!.id)) {
      m.periods.push({
        start: period!.start,
        end: period!.end,
        periodId: period!.id,
        schemeId: scheme!.id,
      });
    }
    m.updatedAt = nowIso();
  }
  const payment: Payment = {
    id: genId("pay"),
    hasherId: h.id,
    kennelId: kennel.id,
    periodId: period!.id,
    kind: "membership",
    amount: period!.price,
    method: "cash",
    state: "paid",
    recordedByHasherId: c.hasherId,
    createdAt: nowIso(),
  };
  db.payments.push(payment);
  const membership = db.memberships.find(
    (m) => m.hasherId === h.id && m.kennelId === kennel.id
  )!;
  persist();
  return { membership, payment, period };
});

// =============================================================================
// Trails + special events
// =============================================================================

route("GET", "/trails", ({ query }) => {
  auth();
  const db = getDb();
  const direction = query.get("direction");
  if (direction === "upcoming" || direction === "past") {
    const limit = Number(query.get("limit") ?? 25);
    const afterDate = query.get("afterDate");
    const afterId = query.get("afterId");
    let trails = [...db.trails];
    if (direction === "upcoming") {
      trails = trails
        .filter((t) => t.date >= today())
        .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
    } else {
      trails = trails
        .filter((t) => t.date < today())
        .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));
    }
    if (afterDate && afterId) {
      const idx = trails.findIndex((t) => t.id === afterId && t.date === afterDate);
      if (idx >= 0) trails = trails.slice(idx + 1);
    }
    const page = trails.slice(0, limit);
    return { trails: page.map(listShape), hasMore: trails.length > limit };
  }
  const from = query.get("from") ?? "0000-01-01";
  const to = query.get("to") ?? "9999-12-31";
  const trails = db.trails
    .filter((t) => t.date >= from && t.date <= to)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(listShape);
  return { trails };
});

route("GET", "/trails/:id", ({ params }) => {
  auth();
  const db = getDb();
  const trail = findTrail(params.id);
  const kennel = db.kennels.find((k) => k.id === trail.kennelId);
  const attendance = db.attendance.filter((a) => a.trailId === trail.id);
  const hasherIds = new Set<string>([
    ...trail.hareHasherIds,
    ...attendance.map((a) => a.hasherId),
  ]);
  const hashers = db.hashers.filter((h) => hasherIds.has(h.id));
  const payments = db.payments.filter((p) => p.trailId === trail.id);
  const memberships = db.memberships.filter(
    (m) => m.kennelId === trail.kennelId && hasherIds.has(m.hasherId)
  );
  return { trail, kennel, attendance, hashers, payments, memberships };
});

route("POST", "/trails", ({ body }) => {
  const c = auth();
  const db = getDb();
  // Create inline new hashers first so they can be hares.
  const hareIds: string[] = [...(body.hareHasherIds ?? [])];
  for (const nh of body.newHares ?? []) {
    const kennel = findKennel(nh.homeKennelId);
    const h: Hasher = {
      id: genId("h"),
      name: nh.name.trim(),
      homeKennelId: kennel.id,
      homeKennel: kennel.name,
      phone: nh.phone,
      role: "hare",
      visitor: !kennel.managed,
      accountState: nh.phone ? "unclaimed" : "lite",
      createdAt: nowIso(),
    };
    db.hashers.push(h);
    hareIds.push(h.id);
  }
  const kennelId =
    body.kennelId ?? (hareIds[0] && findHasher(hareIds[0]).homeKennelId);
  if (!kennelId) fail(400, "A kennel is required when there is no hare");
  const kennel = findKennel(kennelId);
  requireMismanOf(c, kennel.id);

  const isSpecial = body.eventType === "special";
  let trail: Trail;
  if (isSpecial) {
    const yr = body.date.slice(0, 4);
    const lc = body.name.trim().toLowerCase();
    const clash = db.trails.some(
      (t) =>
        t.eventType === "special" &&
        t.kennelId === kennel.id &&
        t.date.slice(0, 4) === yr &&
        (t.name ?? "").toLowerCase() === lc
    );
    if (clash) {
      fail(409, `${body.name} already exists in ${yr}`, "DUPLICATE_NAME");
    }
    trail = {
      id: `${kennel.id}#${genId("e").slice(2)}`,
      eventType: "special",
      kennelId: kennel.id,
      kennel: kennelDisplayName(kennel),
      name: body.name.trim(),
      date: body.date,
      startAt: body.startAt ?? trailStartAt(body.date) ?? undefined,
      hareHasherIds: hareIds,
      everHadHare: hareIds.length > 0,
      startLocation: body.startLocation ?? "",
      description: body.description,
      specialFee: body.specialFee,
      memberPrice: body.memberPrice,
      nonMemberPrice: body.nonMemberPrice,
      version: 0,
      createdAt: nowIso(),
      createdByHasherId: c.hasherId,
    };
  } else {
    const number = Number(body.number);
    if (
      db.trails.some(
        (t) => t.kennelId === kennel.id && t.number === number
      )
    ) {
      fail(409, `Trail #${number} already exists`, "DUPLICATE_NUMBER");
    }
    trail = {
      id: `${kennel.id}#${number}`,
      eventType: "trail",
      kennelId: kennel.id,
      kennel: kennelDisplayName(kennel),
      number,
      date: body.date,
      startAt: body.startAt ?? trailStartAt(body.date) ?? undefined,
      hareHasherIds: hareIds,
      everHadHare: hareIds.length > 0,
      startLocation: body.startLocation ?? "",
      title: body.title,
      version: 0,
      createdAt: nowIso(),
      createdByHasherId: c.hasherId,
    };
  }
  db.trails.push(trail);
  // Auto-check-in the hares (mirrors Story 8.9).
  for (const hid of hareIds) autoCheckInHare(trail, hid, c.hasherId);
  persist();
  return trail;
});

function autoCheckInHare(trail: Trail, hasherId: string, by: string): void {
  const db = getDb();
  const h = db.hashers.find((x) => x.id === hasherId);
  if (!h) return;
  if (db.attendance.some((a) => a.trailId === trail.id && a.hasherId === hasherId)) {
    return;
  }
  db.attendance.push({
    trailId: trail.id,
    hasherId,
    nameSnapshot: h.name,
    paid: false,
    virgin: false,
    visitor: h.visitor || h.homeKennelId !== trail.kennelId,
    checkedInAt: nowIso(),
    checkedInByHasherId: by,
  });
  recomputeLastAttended(hasherId);
}

route("PATCH", "/trails/:id/details", ({ params, body }) => {
  const c = auth();
  const trail = findTrail(params.id);
  const version = trail.version ?? 0;
  if (!body.overwrite && body.version !== version) {
    fail(409, "Someone else saved changes first", "VERSION_CONFLICT", {
      current: trail,
    });
  }
  const fields = [
    "startLocation",
    "startLocationUrl",
    "transit",
    "title",
    "description",
    "hareNotes",
    "onIn",
    "aa",
    "bringStandard",
    "bringCustom",
    "specialFee",
    "memberPrice",
    "nonMemberPrice",
  ] as const;
  const changes: TrailHistoryEntry["changes"] = [];
  for (const f of fields) {
    if (body[f] !== undefined && body[f] !== (trail as any)[f]) {
      changes.push({ field: f, from: (trail as any)[f], to: body[f] });
      (trail as any)[f] = body[f];
    }
  }
  trail.version = version + 1;
  if (changes.length) {
    getDb().trailHistory.push({
      trailId: trail.id,
      at: nowIso(),
      byHasherId: c.hasherId,
      version: trail.version,
      changes,
    });
  }
  persist();
  return trail;
});

route("PATCH", "/trails/:id", ({ params, body }) => {
  const c = auth();
  const trail = findTrail(params.id);
  requireMismanOf(c, trail.kennelId);
  if (body.date !== undefined) trail.date = body.date;
  if (body.startAt !== undefined) trail.startAt = body.startAt;
  persist();
  return trail;
});

route("GET", "/trails/:id/history", ({ params }) => {
  auth();
  const history = getDb()
    .trailHistory.filter((h) => h.trailId === params.id)
    .sort((a, b) => b.at.localeCompare(a.at));
  return { history };
});

route("POST", "/trails/:id/hares", ({ params, body }) => {
  const c = auth();
  const trail = findTrail(params.id);
  const h = findHasher(body.hasherId);
  if (!trail.hareHasherIds.includes(h.id)) {
    trail.hareHasherIds.push(h.id);
    trail.everHadHare = true;
    autoCheckInHare(trail, h.id, c.hasherId);
  }
  persist();
  return trail;
});

route("DELETE", "/trails/:id/hares/:hasherId", ({ params }) => {
  auth();
  const trail = findTrail(params.id);
  trail.hareHasherIds = trail.hareHasherIds.filter((id) => id !== params.hasherId);
  persist();
  return trail;
});

route("POST", "/trails/:id/reset", ({ params }) => {
  const c = auth();
  const trail = findTrail(params.id);
  requireMismanOf(c, trail.kennelId);
  if (trail.eventType === "special") fail(400, "Special events can't be reset");
  trail.hareHasherIds = [];
  trail.everHadHare = false;
  persist();
  return trail;
});

route("DELETE", "/trails/:id", ({ params }) => {
  const c = auth();
  const db = getDb();
  const trail = findTrail(params.id);
  requireAdmin(c);
  db.trails = db.trails.filter((t) => t.id !== trail.id);
  db.attendance = db.attendance.filter((a) => a.trailId !== trail.id);
  db.payments = db.payments.filter((p) => p.trailId !== trail.id);

  // Renumber only future numbered trails so the sequence stays gap-free.
  let renumbered = 0;
  if (trail.eventType !== "special" && typeof trail.number === "number" && trail.date >= today()) {
    const later = db.trails
      .filter(
        (t) =>
          t.kennelId === trail.kennelId &&
          t.eventType !== "special" &&
          typeof t.number === "number" &&
          (t.number as number) > (trail.number as number)
      )
      .sort((a, b) => (a.number as number) - (b.number as number));
    for (const t of later) {
      const oldId = t.id;
      t.number = (t.number as number) - 1;
      t.id = `${t.kennelId}#${t.number}`;
      for (const a of db.attendance) if (a.trailId === oldId) a.trailId = t.id;
      for (const p of db.payments) if (p.trailId === oldId) p.trailId = t.id;
      renumbered += 1;
    }
  }
  persist();
  return { deleted: true, renumbered };
});

// =============================================================================
// Attendance
// =============================================================================

route("POST", "/trails/:id/attendance", ({ params, body }) => {
  const c = auth();
  const db = getDb();
  const trail = findTrail(params.id);
  const h = findHasher(body.hasherId);
  let att = db.attendance.find((a) => a.trailId === trail.id && a.hasherId === h.id);
  if (!att) {
    att = {
      trailId: trail.id,
      hasherId: h.id,
      nameSnapshot: body.nameSnapshot ?? h.name,
      paid: body.paid ?? false,
      virgin: body.virgin ?? !db.attendance.some((a) => a.hasherId === h.id),
      visitor: body.visitor ?? (h.visitor || h.homeKennelId !== trail.kennelId),
      checkedInAt: nowIso(),
      checkedInByHasherId: c.hasherId,
    };
    db.attendance.push(att);
    recomputeLastAttended(h.id);
  }
  if (att.paid) recordEventPayment(trail, h, c.hasherId);
  persist();
  return att;
});

route("PATCH", "/trails/:id/attendance/:hasherId", ({ params, body }) => {
  const c = auth();
  const db = getDb();
  const trail = findTrail(params.id);
  const att = db.attendance.find(
    (a) => a.trailId === params.id && a.hasherId === params.hasherId
  );
  if (!att) fail(404, "Not checked in");
  if (body.paid !== undefined && body.paid !== att.paid) {
    att.paid = body.paid;
    if (body.paid) recordEventPayment(trail, findHasher(params.hasherId), c.hasherId);
    else reverseEventPayment(trail.id, params.hasherId, c.hasherId);
  }
  if (body.virgin !== undefined) att.virgin = body.virgin;
  if (body.visitor !== undefined) att.visitor = body.visitor;
  if (body.nameSnapshot !== undefined) att.nameSnapshot = body.nameSnapshot;
  persist();
  return att;
});

route("DELETE", "/trails/:id/attendance/:hasherId", ({ params }) => {
  const c = auth();
  const db = getDb();
  db.attendance = db.attendance.filter(
    (a) => !(a.trailId === params.id && a.hasherId === params.hasherId)
  );
  reverseEventPayment(params.id, params.hasherId, c.hasherId);
  recomputeLastAttended(params.hasherId);
  persist();
  return { removed: true };
});

// =============================================================================
// Admin
// =============================================================================

route("POST", "/admin/access", ({ body }) => {
  requireAdmin(auth());
  // No Cognito in the demo — just mark the hasher claimed so it reads as having
  // login access. The grant itself is a no-op beyond that.
  const h = getDb().hashers.find((x) => x.id === body.hasherId);
  if (h && body.method === "phone") {
    h.phone = body.identifier;
    h.accountState = "claimed";
    persist();
  }
  return { granted: true };
});

route("PATCH", "/admin/hashers/:id/role", ({ params, body }) => {
  const c = auth();
  requireAdmin(c);
  const h = findHasher(params.id);
  const role = body.role as Role;
  h.role = role;
  auditRole({
    action: role === "admin" ? "grant" : "revoke",
    role: "admin",
    targetHasherId: h.id,
    targetName: h.name,
    actorHasherId: c.hasherId,
    actorName: findHasher(c.hasherId).name,
  });
  persist();
  return h;
});

route("GET", "/admin/role-audit", () => {
  requireAdmin(auth());
  const records = [...getDb().roleAudit].sort((a, b) => b.at.localeCompare(a.at));
  return { records };
});

// =============================================================================
// Reports (Story 9.3)
// =============================================================================

route("GET", "/reports/attendance", ({ query }) => {
  const c = auth();
  const db = getDb();
  const kennelIds = (query.get("kennelIds") ?? "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
  if (kennelIds.length === 0) fail(400, "kennelIds is required");
  const from = query.get("from");
  const to = query.get("to");
  if (!from || !to) fail(400, "from and to are required");
  if (from > to) fail(400, "from must be on or before to");
  for (const kennelId of kennelIds) requireMismanOf(c, kennelId);

  const kennelSet = new Set(kennelIds);
  const events = db.trails.filter(
    (t) => kennelSet.has(t.kennelId) && t.date >= from && t.date <= to
  );

  const attendeeIds = new Set<string>();
  let totalAttendance = 0;
  let totalMoneyCollected = 0;
  const rows: ReportTrailRow[] = events.map((trail) => {
    const att = db.attendance.filter((a) => a.trailId === trail.id);
    for (const a of att) attendeeIds.add(a.hasherId);
    const moneyCollected = db.payments
      .filter((p) => p.trailId === trail.id && p.state === "paid")
      .reduce((sum, p) => sum + p.amount, 0);
    totalAttendance += att.length;
    totalMoneyCollected += moneyCollected;
    return {
      trailId: trail.id,
      eventType: trail.eventType,
      kennelId: trail.kennelId,
      kennel: trail.kennel,
      number: trail.number,
      name: trail.name,
      date: trail.date,
      attendance: att.length,
      moneyCollected,
    };
  });
  rows.sort(
    (a, b) => a.date.localeCompare(b.date) || a.trailId.localeCompare(b.trailId)
  );

  // Active = an active membership (as of `to`) in any selected kennel.
  let activeMembers = 0;
  for (const hasherId of attendeeIds) {
    const isActive = kennelIds.some(
      (kennelId) =>
        membershipState(membershipOf(hasherId, kennelId), to) === "active"
    );
    if (isActive) activeMembers += 1;
  }

  const trailCount = events.length;
  const uniqueAttendees = attendeeIds.size;
  const report: AttendanceReport = {
    kennelIds,
    from,
    to,
    trailCount,
    totalAttendance,
    avgAttendancePerTrail: trailCount ? totalAttendance / trailCount : 0,
    uniqueAttendees,
    totalMoneyCollected,
    avgMoneyPerTrail: trailCount ? totalMoneyCollected / trailCount : 0,
    activeMembers,
    nonMembers: uniqueAttendees - activeMembers,
    trails: rows,
  };
  return report;
});
